import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RootState } from 'store/index';
import {
  fetchProviders,
  createProvider,
  updateProvider,
  deleteProvider,
} from 'store/paymentProvidersSlice';
import { PaymentProvider, CreateProviderDto, UpdateProviderDto } from 'types/paymentProvider';
import { commonGridStyles } from 'styles/gridStyles';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';
import CreateEditProviderModal from './CreateEditProviderModal';
import styles from './payments.module.scss';
import commonStyles from 'styles/common.module.scss';
import ComponentLoader from 'components/common/ComponentLoader';

const PaymentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useAppSelector((state: RootState) => state.paymentProviders);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<PaymentProvider | null>(null);

  const [deleteModal, setDeleteModal] = useState<{ open: boolean; id: number | ''; title: string }>(
    {
      open: false,
      id: '',
      title: '',
    },
  );

  useEffect(() => {
    dispatch(fetchProviders());
  }, [dispatch]);

  const handleOpenCreateModal = () => {
    setEditingProvider(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (provider: PaymentProvider) => {
    setEditingProvider(provider);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProvider(null);
  };

  const handleSaveProvider = async (formData: CreateProviderDto | UpdateProviderDto) => {
    try {
      if (editingProvider) {
        await dispatch(
          updateProvider({ id: editingProvider.id, data: formData as UpdateProviderDto }),
        ).unwrap();
      } else {
        await dispatch(createProvider(formData as CreateProviderDto)).unwrap();
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save provider:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteProvider(deleteModal.id)).unwrap();
        setDeleteModal({ open: false, id: '', title: '' });
      } catch (error) {
        console.error('Failed to delete provider:', error);
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'code', headerName: 'Code', width: 150 },
    { field: 'name', headerName: 'Name', width: 250 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Typography
          sx={{ color: params.value ? '#4caf50' : '#f44336', mt: 1.5, fontSize: '0.875rem' }}
        >
          {params.value ? 'Active' : 'Inactive'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box className={styles.actions}>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(params.row);
            }}
            sx={{ minWidth: '40px', padding: '4px' }}
          >
            <Edit size={16} />
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ open: true, id: params.row.id, title: params.row.name });
            }}
            sx={{ minWidth: '40px', padding: '4px' }}
          >
            <Trash2 size={16} />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          Payment Providers
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          className={styles.createBtn}
          onClick={handleOpenCreateModal}
        >
          Create
        </Button>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        <DataGrid
          rows={data}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          sx={{ ...commonGridStyles, mt: 6 }}
        />
      </Box>

      <CreateEditProviderModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveProvider}
        initialData={editingProvider}
        isLoading={isLoading}
      />

      <ConfirmDeleteModal
        open={deleteModal.open}
        title={deleteModal.title}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default PaymentsPage;
